import { inject, onBeforeMount, provide, ref } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { Howl } from "howler";
import getDecodedUserId from "@/helpers/get-decoded-user-id";
import Toast from "@/helpers/toast.helper";
import useRole from "@/composables/use-role";

export default function useLeads() {
  const socket = inject("socket");
  const queryClient = useQueryClient();
  const leads = ref([]);

  // Get current user role and ID
  const currentUserId = getDecodedUserId();

  // Create Howl instance for notification sound (handles autoplay restrictions better)
  const notificationSound = ref(null);
  const audioUnlocked = ref(false);

  if (typeof window !== "undefined") {
    notificationSound.value = new Howl({
      src: ["/audio/stairs.mp3"],
      volume: 0.5,
      preload: true,
    });

    // Unlock audio on first user interaction (click, touch, keydown)
    const unlockAudio = () => {
      if (!audioUnlocked.value && notificationSound.value) {
        // Try to play and immediately stop to unlock audio
        const soundId = notificationSound.value.play();
        notificationSound.value.stop(soundId);
        audioUnlocked.value = true;
        // Remove event listeners after unlocking
        document.removeEventListener("click", unlockAudio);
        document.removeEventListener("touchstart", unlockAudio);
        document.removeEventListener("keydown", unlockAudio);
      }
    };

    // Add event listeners for user interaction
    document.addEventListener("click", unlockAudio, { once: true });
    document.addEventListener("touchstart", unlockAudio, { once: true });
    document.addEventListener("keydown", unlockAudio, { once: true });
  }

  // Use the centralized role composable (shares cache, no redundant API calls)
  const { isAgent, isAdmin } = useRole();

  const getLeadId = (lead) => {
    return lead?.id || lead?.leadId || lead?.lead_id;
  };

  // Check if lead is assigned to current agent
  const isLeadAssignedToCurrentAgent = (lead) => {
    // If user is an administrator, always show all leads
    if (isAdmin.value) {
      return true;
    }

    // If not an agent, show all leads (fallback for non-agent, non-admin users)
    if (!isAgent.value) {
      return true;
    }

    // For agents: only show leads assigned to them
    // Get assign_to value - could be an object with id, or direct id, or assign_to_id field
    const assignTo = lead?.assign_to;
    const assignToId = lead?.assign_to_id;

    if (!assignTo && !assignToId) {
      // If no assignment, don't show to agent
      return false;
    }

    // Handle different assign_to formats
    let targetUserId = null;

    if (assignToId) {
      // Direct assign_to_id field
      targetUserId = assignToId;
    } else if (assignTo) {
      // assign_to could be an object with id/user_id, or a direct number/string
      targetUserId =
        assignTo?.id ||
        assignTo?.user_id ||
        (typeof assignTo === "number" ? assignTo : null) ||
        (typeof assignTo === "string" ? parseInt(assignTo) : null);
    }

    // Check if assigned to current agent
    return targetUserId === currentUserId;
  };

  const playNotificationSound = () => {
    if (!notificationSound.value) return;

    try {
      // Howler.js handles autoplay restrictions better
      // Stop any currently playing sound and play from beginning
      notificationSound.value.stop();
      notificationSound.value.play();
    } catch (e) {
      console.error("Error playing notification sound:", e);
    }
  };

  // Check if toast should be shown for a lead
  const shouldShowToast = (lead) => {
    if (!lead) return false;

    // Get assign_to value - could be an object with id, or direct id
    const assignTo = lead?.assign_to;

    // If assign_to is null or empty, show toast only to admins
    if (!assignTo) {
      return isAdmin.value;
    }

    // If assign_to has a value, show toast only to the assigned agent
    // assign_to could be an object with id/user_id, or a direct number/string
    const targetUserId =
      assignTo?.id ||
      assignTo?.user_id ||
      (typeof assignTo === "number" ? assignTo : null) ||
      (typeof assignTo === "string" ? parseInt(assignTo) : null);

    // If we couldn't extract a valid user ID, show only to admins (unassigned)
    if (!targetUserId) {
      return isAdmin.value;
    }

    // Show toast only if assigned to current user
    return targetUserId === currentUserId;
  };

  const addOrUpdateLead = (newLead, isFromSocket = false) => {
    if (!newLead) return;

    const newLeadId = getLeadId(newLead);
    if (!newLeadId) return;

    const isNewLead = newLead?.opened === false;
    const existingIndex = leads.value.findIndex(
      (lead) => getLeadId(lead) === newLeadId
    );

    // Check if lead should be added to the list (for agents, only if assigned)
    // For administrators, always show all leads
    const shouldAddToList = isLeadAssignedToCurrentAgent(newLead);

    // Check if toast should be shown (different logic: unassigned = all, assigned = only assigned agent)
    const shouldShowNotification = shouldShowToast(newLead);

    // For agents only (not administrators): remove lead and invalidate query if not assigned to current agent
    if (isFromSocket && isAgent.value && !isAdmin.value && !shouldAddToList) {
      // Remove from socket leads array immediately for instant UI update
      if (existingIndex !== -1) {
        leads.value.splice(existingIndex, 1);
      }

      // Invalidate leads list query to refresh API data
      queryClient.invalidateQueries({
        queryKey: ["QUERY_GET_LEADS"],
      });
      return;
    }

    if (existingIndex !== -1) {
      const existingLead = leads.value[existingIndex];
      const previousWasNewLead = existingLead?.opened === false;

      // Only update the list if the lead should be shown
      if (shouldAddToList) {
        leads.value[existingIndex] = newLead;
      }

      // Trigger notification for new leads
      // Show toast if: from socket, is new lead, wasn't new before, and should show notification
      if (
        isFromSocket &&
        isNewLead &&
        !previousWasNewLead &&
        shouldShowNotification
      ) {
        const leadName =
          newLead.name ||
          newLead.lead_name ||
          newLead.user?.name ||
          "Unknown Lead";
        playNotificationSound();
        Toast.info(`New lead: ${leadName}`);
      }
    } else {
      // Only add to list if the lead should be shown
      if (shouldAddToList) {
        leads.value = [newLead, ...leads.value];
      }

      // Trigger notification for new leads
      // Show toast if: from socket, is new lead, and should show notification
      if (isFromSocket && isNewLead && shouldShowNotification) {
        const leadName =
          newLead.name ||
          newLead.lead_name ||
          newLead.user?.name ||
          "Unknown Lead";
        playNotificationSound();
        Toast.info(`New lead: ${leadName}`);
      }
    }
  };

  onBeforeMount(() => {
    socket?.on("CRM\\NewLeads", (data) => {
      const incomingLead = data.leads || data.lead || data;

      // Handle single lead or array of leads
      if (Array.isArray(incomingLead)) {
        incomingLead.forEach((lead) => {
          addOrUpdateLead(lead, true); // Mark as from socket
        });
      } else {
        addOrUpdateLead(incomingLead, true); // Mark as from socket
      }
    });
  });
  provide("leads", leads);
}
